// Outlet settings (slice 26, ADR 0027). Minimal first-pass UI: one
// text input for the Google review URL, Save/Clear, surfaced errors.
// More settings will land here as Phase 3 / Phase 4 grow (loyalty
// toggles, campaign defaults, etc.).

import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../api/client.js';
import type { MeResponse, OutletSettings, UpdateOutletSettingsBody } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { hasPermission } from '../auth/permissions.js';

export function OutletSettingsPage(): JSX.Element {
  const { orgId, outletId } = useParams<{ orgId: string; outletId: string }>();
  const { user } = useAuth();
  const qc = useQueryClient();

  const settingsKey = ['outlet-settings', orgId, outletId] as const;
  const settings = useQuery({
    queryKey: settingsKey,
    queryFn: () => apiFetch<OutletSettings>(`/organizations/${orgId}/outlets/${outletId}/settings`),
    enabled: !!orgId && !!outletId,
  });

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeResponse>('/me'),
    staleTime: 30_000,
  });

  const canEdit = (() => {
    if (!me.data || !outletId) return false;
    const membership = me.data.memberships.find((m) => m.outlet.id === outletId);
    return membership ? hasPermission(membership.permissions, 'menu:edit') : false;
  })();

  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) {
      setGoogleReviewUrl(settings.data.googleReviewUrl ?? '');
      setFieldError(null);
    }
  }, [settings.data]);

  const save = useMutation({
    mutationFn: (body: UpdateOutletSettingsBody) =>
      apiFetch<OutletSettings>(`/organizations/${orgId}/outlets/${outletId}/settings`, {
        method: 'PUT',
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(settingsKey, data);
      setFieldError(null);
    },
  });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    const trimmed = googleReviewUrl.trim();
    save.mutate({ googleReviewUrl: trimmed.length === 0 ? null : trimmed });
  }

  function onClear(): void {
    setGoogleReviewUrl('');
    save.mutate({ googleReviewUrl: null });
  }

  const errorMessage = fieldError ?? mutationErrorMessage(save.error);

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Outlet settings</h1>
          <p className="muted">
            Signed in as <strong>{user?.email}</strong> ·{' '}
            <Link to={`/orgs/${orgId}/outlets/${outletId}/menus`}>Back to menus</Link>
          </p>
        </div>
      </header>

      {settings.isLoading ? <p>Loading…</p> : null}
      {settings.error && !settings.data ? <p className="error">Could not load settings.</p> : null}

      {settings.data ? (
        <section className="card settings-card">
          <h2>Google review link</h2>
          <p className="muted">
            Paste the "share link" from your Google Business Profile. Customers tap through to
            Google and post the review themselves — we never post for them.
          </p>
          <form onSubmit={onSubmit} className="settings-form">
            <label htmlFor="googleReviewUrl">Review URL</label>
            <input
              id="googleReviewUrl"
              type="url"
              inputMode="url"
              placeholder="https://g.page/r/..."
              value={googleReviewUrl}
              onChange={(e) => setGoogleReviewUrl(e.target.value)}
              disabled={!canEdit || save.isPending}
            />
            <div className="row-actions">
              <button type="submit" disabled={!canEdit || save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
              {settings.data.googleReviewUrl ? (
                <button type="button" onClick={onClear} disabled={!canEdit || save.isPending}>
                  Clear
                </button>
              ) : null}
            </div>
            {errorMessage ? (
              <p className="error" role="alert">
                {errorMessage}
              </p>
            ) : null}
            {!canEdit ? (
              <p className="muted">You do not have permission to edit settings.</p>
            ) : null}
          </form>
        </section>
      ) : null}
    </main>
  );
}

function mutationErrorMessage(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError) {
    if (error.status === 400) {
      const body = error.body as { details?: { message: string }[] } | undefined;
      const detail = body?.details?.[0]?.message;
      return detail ?? 'That URL does not look right. Try a Google review or g.page link.';
    }
    if (error.status === 403) return 'You do not have permission to do that.';
    if (error.status === 404) return 'This outlet no longer exists.';
    if (error.status === 0) return 'Network error — check your connection.';
    return 'Could not save settings.';
  }
  return 'Could not save settings.';
}
